// =============================================================================
// MeuDriver — Instructor Onboarding Wizard (5 Steps)
// =============================================================================

import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Upload,
  X,
  Car,
  Check,
  Loader2,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import { useCpfValidation, maskCpf } from '@/hooks/useCpfValidation'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Progress } from '@/components/ui/progress'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { SPECIALTY_LABELS, DAY_OF_WEEK_LABELS } from '@/lib/constants'
import type { SpecialtyType } from '@/types/database'

// ---------------------------------------------------------------------------
// Zod schemas per step
// ---------------------------------------------------------------------------

const step1Schema = z.object({
  bio: z
    .string()
    .min(30, 'Escreva pelo menos 30 caracteres sobre você')
    .max(1000, 'Máximo de 1000 caracteres'),
  years_experience: z.coerce
    .number()
    .int()
    .min(1, 'Mínimo de 1 ano')
    .max(60, 'Máximo de 60 anos'),
})

const step2Schema = z.object({
  cnh_number: z.string().min(1, 'Informe o número da CNH'),
  vehicle_model: z.string().min(2, 'Informe o modelo do veículo'),
  vehicle_year: z.coerce.number().int().min(1990).max(2030),
  vehicle_plate: z
    .string()
    .regex(
      /^[A-Z]{3}[-]?\d{1}[A-Z0-9]{1}\d{2}$|^[A-Z]{3}[-]?\d{4}$/,
      'Placa inválida. Use ABC1234 ou ABC1D23',
    ),
})

const step4Schema = z.object({
  hourly_rate: z.coerce.number().min(20, 'Mínimo R$ 20').max(500, 'Máximo R$ 500'),
  service_radius_km: z.coerce
    .number()
    .int()
    .min(5, 'Mínimo 5 km')
    .max(100, 'Máximo 100 km'),
})

type Step1Data = z.infer<typeof step1Schema>
type Step2Data = z.infer<typeof step2Schema>
type Step4Data = z.infer<typeof step4Schema>

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DayAvail {
  enabled: boolean
  start_time: string
  end_time: string
}

type WeekAvail = Record<number, DayAvail>

const defaultWeekAvail: WeekAvail = Object.fromEntries(
  [0, 1, 2, 3, 4, 5, 6].map((d) => [
    d,
    { enabled: false, start_time: '08:00', end_time: '18:00' },
  ]),
)

const TOTAL_STEPS = 5

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function StepProgress({
  step,
  total,
}: {
  step: number
  total: number
}) {
  const percent = ((step - 1) / (total - 1)) * 100
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-teal-700">
          Passo {step} de {total}
        </span>
        <span className="text-muted-foreground">{Math.round(percent)}% concluído</span>
      </div>
      <Progress value={percent} className="h-2" />
      <div className="flex justify-between">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold transition-all',
              i + 1 < step
                ? 'bg-teal-600 text-white'
                : i + 1 === step
                ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-500'
                : 'bg-gray-100 text-gray-400',
            )}
          >
            {i + 1 < step ? <Check className="h-3 w-3" /> : i + 1}
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function InstructorOnboarding() {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  // Collected data across steps
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null)
  const [step2Data, setStep2Data] = useState<Step2Data | null>(null)
  const [vehiclePhotos, setVehiclePhotos] = useState<string[]>([])
  const [step4Data, setStep4Data] = useState<Step4Data | null>(null)
  const [selectedSpecialties, setSelectedSpecialties] = useState<Set<SpecialtyType>>(new Set())
  const [weekAvail, setWeekAvail] = useState<WeekAvail>({ ...defaultWeekAvail })

  // CPF (obrigatório para instrutores)
  const [cpfRaw, setCpfRaw] = useState('')
  const cpfValidation = useCpfValidation(cpfRaw, false)

  const photosInputRef = useRef<HTMLInputElement>(null)
  const [uploadingPhotos, setUploadingPhotos] = useState(false)

  // ── Step forms ────────────────────────────────────────────────────────────

  const form1 = useForm<Step1Data>({ resolver: zodResolver(step1Schema) })
  const form2 = useForm<Step2Data>({ resolver: zodResolver(step2Schema) })
  const form4 = useForm<Step4Data>({ resolver: zodResolver(step4Schema) })

  // ── Photo upload (step 3) ─────────────────────────────────────────────────

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!user?.id) return
    const files = Array.from(e.target.files ?? [])
    if (vehiclePhotos.length + files.length > 4) {
      toast.warning('Máximo de 4 fotos.')
      return
    }
    setUploadingPhotos(true)
    const urls: string[] = []
    for (const file of files) {
      const ext = file.name.split('.').pop()
      const path = `vehicles/${user.id}/${Date.now()}.${ext}`
      const { error } = await supabase.storage
        .from('instructors')
        .upload(path, file, { upsert: false })
      if (!error) {
        const { data } = supabase.storage.from('instructors').getPublicUrl(path)
        urls.push(data.publicUrl)
      }
    }
    setVehiclePhotos((prev) => [...prev, ...urls])
    setUploadingPhotos(false)
    if (e.target) e.target.value = ''
  }

  function removePhoto(url: string) {
    setVehiclePhotos((prev) => prev.filter((p) => p !== url))
  }

  // ── Step navigation ───────────────────────────────────────────────────────

  async function handleNext() {
    if (step === 1) {
      const valid = await form1.trigger()
      if (!valid) return

      // CPF obrigatório para instrutores
      const cpfDigits = cpfRaw.replace(/\D/g, '')
      if (cpfDigits.length === 0) {
        toast.error('CPF é obrigatório para instrutores.')
        return
      }
      if (!cpfValidation.valid) {
        toast.error(cpfValidation.message || 'CPF inválido.')
        return
      }

      setStep1Data(form1.getValues())
      setStep(2)
    } else if (step === 2) {
      const valid = await form2.trigger()
      if (!valid) return
      setStep2Data(form2.getValues())
      setStep(3)
    } else if (step === 3) {
      if (vehiclePhotos.length === 0) {
        toast.warning('Adicione pelo menos 1 foto do veículo.')
        return
      }
      setStep(4)
    } else if (step === 4) {
      const valid = await form4.trigger()
      if (!valid) return
      if (selectedSpecialties.size === 0) {
        toast.warning('Selecione pelo menos uma especialidade.')
        return
      }
      setStep4Data(form4.getValues())
      setStep(5)
    } else if (step === 5) {
      await handleSubmit()
    }
  }

  function handleBack() {
    if (step > 1) setStep((s) => s - 1)
  }

  // ── Final submit ──────────────────────────────────────────────────────────

  async function handleSubmit() {
    if (!user?.id || !step1Data || !step2Data || !step4Data) return
    setSubmitting(true)

    try {
      // 1. Get or create instructor row
      let { data: inst } = await supabase
        .from('instructors')
        .select('id')
        .eq('profile_id', user.id)
        .single()

      if (!inst) {
        const { data: newInst, error: createErr } = await supabase
          .from('instructors')
          .insert({ profile_id: user.id })
          .select('id')
          .single()
        if (createErr) throw createErr
        inst = newInst
      }

      const instructorId = inst!.id

      // 2. Update instructor record
      const { error: updateErr } = await supabase
        .from('instructors')
        .update({
          bio: step1Data.bio,
          years_experience: step1Data.years_experience,
          cnh_number: step2Data.cnh_number,
          vehicle_model: step2Data.vehicle_model,
          vehicle_year: step2Data.vehicle_year,
          vehicle_plate: step2Data.vehicle_plate.toUpperCase(),
          vehicle_photos: vehiclePhotos,
          hourly_rate: step4Data.hourly_rate,
          service_radius_km: step4Data.service_radius_km,
          status: 'pending',
        })
        .eq('id', instructorId)

      if (updateErr) throw updateErr

      // 3. Upsert specialties
      await supabase
        .from('instructor_specialties')
        .delete()
        .eq('instructor_id', instructorId)

      const specInserts = Array.from(selectedSpecialties).map((s) => ({
        instructor_id: instructorId,
        specialty: s,
      }))
      if (specInserts.length > 0) {
        await supabase.from('instructor_specialties').insert(specInserts)
      }

      // 4. Upsert availability
      await supabase.from('availability').delete().eq('instructor_id', instructorId)

      const availInserts = Object.entries(weekAvail)
        .filter(([, v]) => v.enabled)
        .map(([day, v]) => ({
          instructor_id: instructorId,
          day_of_week: Number(day),
          start_time: v.start_time + ':00',
          end_time: v.end_time + ':00',
        }))

      if (availInserts.length > 0) {
        await supabase.from('availability').insert(availInserts)
      }

      // 5. Salva CPF hasheado
      const cpfDigits = cpfRaw.replace(/\D/g, '')
      if (cpfDigits.length === 11) {
        const { error: cpfError } = await supabase.rpc('save_cpf', { p_cpf: cpfDigits })
        if (cpfError && import.meta.env.DEV) {
          console.warn('[onboarding] CPF save error:', cpfError.message)
        }
      }

      setDone(true)
    } catch (err) {
      toast.error('Erro ao enviar cadastro. Tente novamente.')
      Sentry.captureException(err, { tags: { source: 'InstructorOnboarding.submit' } })
      if (import.meta.env.DEV) {
        console.error(err)
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ── Success screen ────────────────────────────────────────────────────────

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-white p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-teal-100">
              <CheckCircle className="h-10 w-10 text-teal-600" />
            </div>
          </div>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cadastro enviado!</h1>
            <p className="text-muted-foreground mt-2">
              Recebemos suas informações. Nossa equipe irá analisar e você receberá
              a aprovação em breve.
            </p>
          </div>

          {/* Checklist */}
          <Card>
            <CardContent className="p-5 text-left space-y-3">
              <p className="font-medium text-sm text-gray-700 mb-3">
                O que foi enviado:
              </p>
              {[
                { label: 'Informações pessoais e bio', done: Boolean(step1Data) },
                { label: 'CNH e dados do veículo', done: Boolean(step2Data) },
                {
                  label: `${vehiclePhotos.length} foto(s) do veículo`,
                  done: vehiclePhotos.length > 0,
                },
                {
                  label: `${selectedSpecialties.size} especialidade(s)`,
                  done: selectedSpecialties.size > 0,
                },
                {
                  label: `Disponibilidade semanal (${Object.values(weekAvail).filter((v) => v.enabled).length} dias)`,
                  done: true,
                },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <CheckCircle
                    className={`h-4 w-4 shrink-0 ${item.done ? 'text-teal-600' : 'text-gray-300'}`}
                  />
                  <span className="text-sm text-gray-700">{item.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Button
            className="w-full"
            onClick={() => navigate('/instrutor/dashboard')}
          >
            Ir para o dashboard
          </Button>
        </div>
      </div>
    )
  }

  // ── Wizard layout ─────────────────────────────────────────────────────────

  const stepTitles = [
    'Sobre você',
    'Habilitação e veículo',
    'Fotos do veículo',
    'Serviços e valores',
    'Disponibilidade',
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-start justify-center p-4 pt-10">
      <div className="w-full max-w-xl space-y-6">
        {/* Brand header */}
        <div className="flex items-center gap-2 justify-center">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600 text-white">
            <Car className="h-4 w-4" />
          </div>
          <span className="font-bold text-xl text-teal-700">MeuDriver</span>
        </div>

        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Cadastro de Instrutor</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Complete seu perfil para começar a receber alunos.
          </p>
        </div>

        {/* Progress */}
        <StepProgress step={step} total={TOTAL_STEPS} />

        {/* Step card */}
        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-5">
              Passo {step}: {stepTitles[step - 1]}
            </h2>

            {/* ── STEP 1: Bio ─────────────────────────────────────────────── */}
            {step === 1 && (
              <div className="space-y-4">
                {/* CPF — obrigatório para instrutores */}
                <div className="space-y-1.5">
                  <Label htmlFor="cpf">
                    CPF <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="cpf"
                      type="text"
                      inputMode="numeric"
                      placeholder="000.000.000-00"
                      value={cpfRaw}
                      onChange={(e) => setCpfRaw(maskCpf(e.target.value))}
                      maxLength={14}
                      className={`h-10 ${cpfRaw && !cpfValidation.valid && !cpfValidation.checking ? 'border-red-400' : cpfValidation.valid && cpfRaw ? 'border-green-400' : ''}`}
                      data-testid="cpf-input-onboarding"
                    />
                    {cpfValidation.checking && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                    )}
                  </div>
                  {cpfRaw && !cpfValidation.checking && cpfValidation.message && (
                    <p className="text-xs text-red-500">{cpfValidation.message}</p>
                  )}
                  {cpfRaw && !cpfValidation.checking && cpfValidation.valid && (
                    <p className="text-xs text-green-600">CPF disponivel.</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Seu CPF e armazenado de forma segura (hash SHA-256) e nunca exibido publicamente.
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="bio">Sobre você *</Label>
                  <Textarea
                    id="bio"
                    rows={5}
                    placeholder="Conte sobre sua experiência como instrutor, metodologia de ensino e o que diferencia você..."
                    {...form1.register('bio')}
                  />
                  {form1.formState.errors.bio && (
                    <p className="text-xs text-red-500">
                      {form1.formState.errors.bio.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {form1.watch('bio')?.length ?? 0}/1000 caracteres
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="years_experience">Anos de experiência *</Label>
                  <Input
                    id="years_experience"
                    type="number"
                    min={1}
                    max={60}
                    placeholder="Ex: 5"
                    {...form1.register('years_experience')}
                  />
                  {form1.formState.errors.years_experience && (
                    <p className="text-xs text-red-500">
                      {form1.formState.errors.years_experience.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 2: CNH + Vehicle ────────────────────────────────────── */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="cnh_number">Número da CNH *</Label>
                  <Input
                    id="cnh_number"
                    placeholder="Ex: 12345678900"
                    {...form2.register('cnh_number')}
                  />
                  {form2.formState.errors.cnh_number && (
                    <p className="text-xs text-red-500">
                      {form2.formState.errors.cnh_number.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="vehicle_model">Modelo do veículo *</Label>
                  <Input
                    id="vehicle_model"
                    placeholder="Ex: Fiat Argo 1.0"
                    {...form2.register('vehicle_model')}
                  />
                  {form2.formState.errors.vehicle_model && (
                    <p className="text-xs text-red-500">
                      {form2.formState.errors.vehicle_model.message}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="vehicle_year">Ano *</Label>
                    <Input
                      id="vehicle_year"
                      type="number"
                      min={1990}
                      max={2030}
                      placeholder={String(new Date().getFullYear())}
                      {...form2.register('vehicle_year')}
                    />
                    {form2.formState.errors.vehicle_year && (
                      <p className="text-xs text-red-500">
                        {form2.formState.errors.vehicle_year.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="vehicle_plate">Placa *</Label>
                    <Input
                      id="vehicle_plate"
                      placeholder="ABC1234"
                      className="uppercase"
                      {...form2.register('vehicle_plate')}
                      onChange={(e) => {
                        const upper = e.target.value.toUpperCase()
                        form2.setValue('vehicle_plate', upper)
                      }}
                    />
                    {form2.formState.errors.vehicle_plate && (
                      <p className="text-xs text-red-500">
                        {form2.formState.errors.vehicle_plate.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: Vehicle Photos ───────────────────────────────────── */}
            {step === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Adicione fotos do veículo que será utilizado nas aulas. Isso aumenta
                  a confiança dos alunos. Mínimo 1 foto, máximo 4.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {vehiclePhotos.map((url, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={url}
                        alt={`Foto ${i + 1}`}
                        className="h-32 w-full object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(url)}
                        className="absolute top-1 right-1 rounded-full bg-black/60 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}

                  {vehiclePhotos.length < 4 && (
                    <button
                      type="button"
                      onClick={() => photosInputRef.current?.click()}
                      disabled={uploadingPhotos}
                      className="h-32 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-teal-400 hover:text-teal-600 transition-colors"
                    >
                      <Upload className="h-6 w-6" />
                      <span className="text-xs">
                        {uploadingPhotos ? 'Enviando...' : 'Adicionar foto'}
                      </span>
                    </button>
                  )}
                </div>

                <input
                  ref={photosInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                />

                {vehiclePhotos.length === 0 && (
                  <p className="text-xs text-red-500">
                    Ao menos 1 foto é obrigatória.
                  </p>
                )}
              </div>
            )}

            {/* ── STEP 4: Specialties + Rate ───────────────────────────────── */}
            {step === 4 && (
              <div className="space-y-5">
                {/* Rate */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="hourly_rate">Valor por hora (R$) *</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      min={20}
                      max={500}
                      step={5}
                      placeholder="80"
                      {...form4.register('hourly_rate')}
                    />
                    {form4.formState.errors.hourly_rate && (
                      <p className="text-xs text-red-500">
                        {form4.formState.errors.hourly_rate.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="service_radius_km">Raio de atendimento (km) *</Label>
                    <Input
                      id="service_radius_km"
                      type="number"
                      min={5}
                      max={100}
                      placeholder="20"
                      {...form4.register('service_radius_km')}
                    />
                    {form4.formState.errors.service_radius_km && (
                      <p className="text-xs text-red-500">
                        {form4.formState.errors.service_radius_km.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Specialties */}
                <div className="space-y-2">
                  <Label>Especialidades *</Label>
                  <p className="text-xs text-muted-foreground">
                    Selecione pelo menos uma.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(Object.entries(SPECIALTY_LABELS) as [SpecialtyType, string][]).map(
                      ([key, label]) => {
                        const checked = selectedSpecialties.has(key)
                        return (
                          <label
                            key={key}
                            className={cn(
                              'flex items-center gap-2.5 rounded-lg border-2 px-3 py-2 cursor-pointer text-sm transition-colors',
                              checked
                                ? 'border-teal-500 bg-teal-50 text-teal-800 font-medium'
                                : 'border-gray-200 hover:border-gray-300',
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setSelectedSpecialties((prev) => {
                                  const next = new Set(prev)
                                  if (e.target.checked) next.add(key)
                                  else next.delete(key)
                                  return next
                                })
                              }}
                              className="rounded text-teal-600"
                            />
                            {label}
                          </label>
                        )
                      },
                    )}
                  </div>
                  {selectedSpecialties.size === 0 && (
                    <p className="text-xs text-red-500">Selecione ao menos uma especialidade.</p>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 5: Availability ─────────────────────────────────────── */}
            {step === 5 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Configure os dias e horários em que você está disponível para aulas.
                  Você poderá ajustar isso depois na página de Agenda.
                </p>

                {[1, 2, 3, 4, 5, 6, 0].map((dow) => {
                  const avail = weekAvail[dow]
                  return (
                    <div key={dow} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="font-medium text-sm">
                          {DAY_OF_WEEK_LABELS[dow]}
                        </Label>
                        <Switch
                          checked={avail.enabled}
                          onCheckedChange={(checked) =>
                            setWeekAvail((prev) => ({
                              ...prev,
                              [dow]: { ...prev[dow], enabled: checked },
                            }))
                          }
                        />
                      </div>
                      {avail.enabled && (
                        <div className="flex items-center gap-3 pl-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-7">De</span>
                            <Input
                              type="time"
                              value={avail.start_time}
                              onChange={(e) =>
                                setWeekAvail((prev) => ({
                                  ...prev,
                                  [dow]: { ...prev[dow], start_time: e.target.value },
                                }))
                              }
                              className="h-8 w-28 text-sm"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-5">às</span>
                            <Input
                              type="time"
                              value={avail.end_time}
                              onChange={(e) =>
                                setWeekAvail((prev) => ({
                                  ...prev,
                                  [dow]: { ...prev[dow], end_time: e.target.value },
                                }))
                              }
                              className="h-8 w-28 text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}

                <p className="text-xs text-muted-foreground pt-2">
                  {Object.values(weekAvail).filter((v) => v.enabled).length === 0
                    ? 'Você pode pular este passo e configurar depois.'
                    : `${Object.values(weekAvail).filter((v) => v.enabled).length} dias selecionados.`}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="flex items-center justify-between pb-10">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1 || submitting}
            className="gap-1.5"
          >
            <ChevronLeft className="h-4 w-4" />
            Voltar
          </Button>

          <Button
            onClick={handleNext}
            disabled={submitting}
            className="gap-1.5 bg-teal-600 hover:bg-teal-700"
          >
            {submitting
              ? 'Enviando...'
              : step === TOTAL_STEPS
              ? 'Concluir cadastro'
              : 'Próximo'}
            {!submitting && step < TOTAL_STEPS && <ChevronRight className="h-4 w-4" />}
            {!submitting && step === TOTAL_STEPS && <CheckCircle className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  )
}
